import type { ResponsiveImageData } from '@/components/atoms/ResponsiveImage';
import type { Product, Variant } from '@/types/custom';
import type { Video_Entry } from '@/types/graphql';
import type { ProductWithImages } from '@/types/query-types';

export function getProductTitle(product: Product) {
    return product.title || '';
}

export function getProductDescription(product: Product) {
    return product.productDescription?.rawHtml || '';
}

/**
 * Get product description with links stripped out.
 * Useful for tiles where the entire card is wrapped in a link.
 */
export function getProductDescriptionText(product: Product) {
    const html = product.productDescription?.rawHtml || '';
    // Replace <a> tags with their text content ([\s\S]*? matches across newlines)
    return html.replace(/<a[^>]*>([\s\S]*?)<\/a>/gi, '$1');
}

export function getProductImages(product: ProductWithImages): ResponsiveImageData[] {
    const images = (product.productImages || []) as Array<ResponsiveImageData | null>;
    return images.filter((img): img is ResponsiveImageData => img !== null && !!img.urlMedium);
}

export function getProductDefaultPrice(product: Product) {
    return product.defaultPrice;
}

export function getProductVariants(product: Product) {
    return product.variants?.map((variant) => {
        return {
            id: Number(variant?.id) || 0,
            price: variant?.price,
            promotionalPrice: variant?.promotionalPrice,
            name: variant?.title,
            stock: variant?.stock || 0,
            hasUnlimitedStock: variant?.hasUnlimitedStock || false,
            inStock: variant?.stock !== 0 || variant?.hasUnlimitedStock,
        } as Variant;
    });
}

export function getLowestPrice(product: Product) {
    // Filter out variants without prices and ensure type safety
    const variantsWithPrices = (product.variants || []).filter(
        (variant): variant is NonNullable<typeof variant> & { price: number } =>
            variant !== null && variant !== undefined && typeof variant.price === 'number'
    );

    // If no variants with prices, return 0
    if (!variantsWithPrices.length) {
        return {
            lowestPrice: 0,
            showFrom: false,
            hasPromotionalPrice: false,
            promotionalPrice: 0,
        };
    }

    // Find the variant with the lowest price
    const lowestPriceVariant = variantsWithPrices.reduce((lowest, current) => {
        if (current.promotionalPrice !== null && current.promotionalPrice !== undefined) {
            return current.promotionalPrice < lowest.price ? current : lowest;
        }
        return current.price < lowest.price ? current : lowest;
    }, variantsWithPrices[0]);

    // Find the highest price variant
    const highestPriceVariant = variantsWithPrices.reduce((max, variant) => {
        return Math.max(max, variant.price);
    }, variantsWithPrices[0].price);

    const hasPromotionalPrice =
        lowestPriceVariant.promotionalPrice !== null &&
        lowestPriceVariant.promotionalPrice !== undefined;

    return {
        lowestPrice: lowestPriceVariant.price,
        showFrom: lowestPriceVariant.price !== highestPriceVariant,
        hasPromotionalPrice,
        promotionalPrice: hasPromotionalPrice ? lowestPriceVariant.promotionalPrice : 0,
    };
}

export function getProductHeaderImage(product: ProductWithImages) {
    return product.landscapeHeaderImage?.[0];
}

export function getProductHeaderVideo(product: Product) {
    return product.headerVideo?.[0] as Video_Entry | undefined;
}
